"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { TbX, TbCamera, TbBuilding } from "react-icons/tb";
import SecondaryButton from "./SecondaryButton";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { useAuth } from "../AuthProvider";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const ProfileDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Estados para a assinatura
  const [signature, setSignature] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [existingSignature, setExistingSignature] = useState(null);
  const [existingSignatureUrl, setExistingSignatureUrl] = useState(null);
  const [isLoadingSignature, setIsLoadingSignature] = useState(false);
  const [driverId, setDriverId] = useState(null);
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(false);
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const canvasRef = useRef(null);
  const signatureTimeoutRef = useRef(null);
  const [isCanvasInitialized, setIsCanvasInitialized] = useState(false);

  // Estados para controle do desenho
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastX, setLastX] = useState(0);
  const [lastY, setLastY] = useState(0);

  // Estado para controlar o loading da imagem no header
  const [headerImageLoading, setHeaderImageLoading] = useState(true);
  const [headerImageError, setHeaderImageError] = useState(false);

  // Estado específico para a imagem no drawer
  const [drawerImageLoading, setDrawerImageLoading] = useState(false);
  const [drawerImageError, setDrawerImageError] = useState(false);

  // Estado para o telefone formatado
  const [formattedPhone, setFormattedPhone] = useState("");


  const [profile, setProfile] = useState({
    username: user.username || "",
    email: user.email || "",
    firstName: user.first_name || "",
    lastName: user.last_name || "",
    phone: user.phone === null || user.phone === "" ? "Not Provided" : user.phone,
    profile_image: user.profile_picture_path || "",
  });

  const [company, setCompany] = useState({
    name: "Cajuu Company",
    email: "contact@cajuu.com",
    phone: "+1 (555) 123-4567",
    address: "123 Business Ave, City, State 12345",
    website: "www.cajuu.com"
  });

  const [file, setFile] = useState(null);
  const drawerRef = useRef(null);
  const navigate = useNavigate();

  // Função para formatar o número de telefone
  const formatPhoneNumber = (phone) => {
    if (!phone || phone === "Not Provided") return "";
    
    // Remove todos os caracteres não numéricos
    const cleaned = phone.replace(/\D/g, '');
    
    // Limita a 10 dígitos
    const limited = cleaned.slice(0, 10);
    
    // Aplica a máscara
    if (limited.length === 0) return "";
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6, 10)}`;
  };

  // Função para remover a formatação
  const removePhoneFormatting = (phone) => {
    if (!phone || phone === "Not Provided") return "";
    return phone.replace(/\D/g, '');
  };

  // Efeito para carregar a imagem do perfil automaticamente no header
  useEffect(() => {
    const loadHeaderImage = () => {
      if (!profile.profile_image) {
        setHeaderImageLoading(false);
        setHeaderImageError(true);
        return;
      }

      const img = new Image();
      const imageUrl = `https://new.cajuu.app/storage/${profile.profile_image}?t=${Date.now()}`;

      img.onload = () => {
        console.log('Imagem do perfil carregada com sucesso no header');
        setHeaderImageLoading(false);
        setHeaderImageError(false);
      };

      img.onerror = () => {
        setHeaderImageLoading(false);
        setHeaderImageError(true);
      };

      img.src = imageUrl;
    };

    loadHeaderImage();
  }, [profile.profile_image]);

  // Efeito para carregar a imagem no drawer quando ele for aberto
  useEffect(() => {
    if (isOpen && profile.profile_image) {
      setDrawerImageLoading(true);
      setDrawerImageError(false);

      const img = new Image();
      const imageUrl = `https://new.cajuu.app/storage/${profile.profile_image}?t=${Date.now()}`;

      img.onload = () => {
        console.log('Imagem do perfil carregada com sucesso no drawer');
        setDrawerImageLoading(false);
        setDrawerImageError(false);
      };

      img.onerror = () => {
        setDrawerImageLoading(false);
        setDrawerImageError(true);
      };

      img.src = imageUrl;
    }
  }, [isOpen, profile.profile_image]);

  // Função para buscar dados do usuário E extrair o driver ID
  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`https://new.cajuu.app/api/users/${user.user_id}`, {
        headers: {
          "Screen-Name": "/Profile",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.data) {
        const userData = response.data.data;

      
        setProfile({
          username: userData.username || "",
          email: userData.email || "",
          firstName: userData.first_name || "",
          lastName: userData.last_name || "",
          phone: userData.phone === null || userData.phone === "" ? "Not Provided" : userData.phone,
          profile_image: userData.profile_picture_path || "",
        });

        // Formatar telefone
        setFormattedPhone(
          userData.phone === null || userData.phone === "" 
            ? "Not Provided" 
            : formatPhoneNumber(userData.phone)
        );

        // EXTRAIR E SALVAR O DRIVER ID
        if (userData.driver && userData.driver.id) {
          const extractedDriverId = userData.driver.id;
          setDriverId(extractedDriverId);
          //console.log('Driver ID encontrado:', extractedDriverId);

          // Salvar também no localStorage para uso global
          localStorage.setItem('driverId', extractedDriverId.toString());
        }
      }
    } catch (error) {
      console.error("Erro ao buscar dados do usuário", error);
      toast.error("Error fetching user data");
    }
  };

  // Buscar dados do usuário quando o componente for montado
  useEffect(() => {
    fetchUserData();
  }, []);

  // Inicializar o canvas
  const initializeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Configurações do canvas
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = 150;

    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Limpar canvas inicialmente
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    setIsCanvasInitialized(true);
  };

  // Função para buscar a assinatura existente do driver usando o driver ID
  const fetchDriverSignature = async () => {
    // Tentar pegar o driverId do estado ou do localStorage
    const currentDriverId = driverId || localStorage.getItem('driverId');

    if (!currentDriverId) {
      console.log("Driver ID not found");
      return;
    }

    setIsLoadingSignature(true);
    try {
      const token = localStorage.getItem("token");

      console.log('Buscando assinatura para driver ID:', currentDriverId);

      const response = await axios.get(`https://new.cajuu.app/api/driver/${currentDriverId}`, {
        headers: {
          "Screen-Name": "/Profile",
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Resposta da API do driver:', response.data);

      if (response.data && response.data.data) {
        const driverData = response.data.data;

        // Verificar se existe uma assinatura no retorno da API
        const signaturePath = driverData.signature || driverData.signature_path || driverData.driver_signature;

        if (signaturePath) {
          console.log('Assinatura encontrada:', signaturePath);
          setExistingSignature(signaturePath);

          // Construir URL completa para exibição
          let fullSignatureUrl;
          if (signaturePath.startsWith('http')) {
            fullSignatureUrl = signaturePath;
          } else if (signaturePath.startsWith('/')) {
            fullSignatureUrl = `https://new.cajuu.app/storage${signaturePath}`;
          } else {
            fullSignatureUrl = `https://new.cajuu.app/storage/${signaturePath}`;
          }
          setExistingSignatureUrl(fullSignatureUrl);

          // Não carregar automaticamente no canvas - só mostrar a imagem
          setShowSignatureCanvas(false);
        } else {
          console.log('Nenhuma assinatura encontrada para este driver');
          setExistingSignatureUrl(null);
          setShowSignatureCanvas(true); // Mostrar canvas se não tiver assinatura
        }
      }
    } catch (error) {
      console.error("Erro ao buscar assinatura do driver:", error);
      setExistingSignatureUrl(null);
      setShowSignatureCanvas(true); // Mostrar canvas em caso de erro
    } finally {
      setIsLoadingSignature(false);
    }
  };

  // Função para carregar assinatura existente no canvas
  const loadExistingSignature = async (signaturePath) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log("Canvas não encontrado");
      return;
    }

    return new Promise((resolve) => {
      try {
        const ctx = canvas.getContext('2d');

        // Criar uma imagem a partir do caminho da assinatura
        const img = new Image();
        img.crossOrigin = "anonymous";

        img.onload = () => {
          console.log("Imagem da assinatura carregada com sucesso");

          // Limpar o canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Calcular dimensões para manter a proporção
          const scale = Math.min(
            canvas.width / img.width,
            canvas.height / img.height
          );
          const width = img.width * scale;
          const height = img.height * scale;
          const x = (canvas.width - width) / 2;
          const y = (canvas.height - height) / 2;

          // Desenhar a imagem da assinatura no canvas
          ctx.drawImage(img, x, y, width, height);

          console.log("Assinatura desenhada no canvas");

          // Marcar que há uma assinatura
          setHasSignature(true);

          // Converter o canvas para File e salvar no estado
          canvas.toBlob((blob) => {
            if (blob) {
              const signatureFile = new File([blob], 'existing_signature.png', { type: 'image/png' });
              setSignature(signatureFile);
              console.log("Assinatura convertida para File");
            }
            resolve();
          }, 'image/png');
        };

        img.onerror = (e) => {
          console.error("Erro ao carregar imagem da assinatura existente:", e);
          resolve();
        };

        // Montar a URL completa da assinatura
        let fullSignatureUrl;
        if (signaturePath.startsWith('http')) {
          fullSignatureUrl = signaturePath;
        } else if (signaturePath.startsWith('/')) {
          fullSignatureUrl = `https://new.cajuu.app/storage${signaturePath}`;
        } else {
          fullSignatureUrl = `https://new.cajuu.app/storage/${signaturePath}`;
        }

        console.log('Carregando assinatura da URL:', fullSignatureUrl);
        img.src = fullSignatureUrl + '?t=' + Date.now(); // Adicionar timestamp para evitar cache

      } catch (error) {
        console.error("Erro ao carregar assinatura existente:", error);
        resolve();
      }
    });
  };

  // Função para mostrar o canvas e carregar a assinatura existente nele
  const showCanvasAndLoadSignature = async () => {
    setShowSignatureCanvas(true);
    // Pequeno delay para garantir que o canvas foi renderizado
    setTimeout(() => {
      if (existingSignature) {
        loadExistingSignature(existingSignature);
      }
    }, 100);
  };

  // Funções para desenhar no canvas
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsDrawing(true);
    setLastX(x);
    setLastY(y);

    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();

    setLastX(x);
    setLastY(y);
  };

  const stopDrawing = () => {
    setIsDrawing(false);

    // Salvar assinatura automaticamente após um pequeno delay
    if (signatureTimeoutRef.current) {
      clearTimeout(signatureTimeoutRef.current);
    }

    signatureTimeoutRef.current = setTimeout(() => {
      saveSignatureAutomatically();
    }, 500);
  };

  // Função para touch events
  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousedown', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    startDrawing(mouseEvent);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    draw(mouseEvent);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    stopDrawing();
  };

  // Função para verificar se o canvas tem assinatura
  const hasCanvasSignature = (canvas) => {
    if (!canvas) return false;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

    for (let i = 0; i < imageData.length; i += 4) {
      if (imageData[i + 3] !== 0) {
        return true;
      }
    }
    return false;
  };

  // Função para salvar a assinatura automaticamente COMO IMAGEM
  const saveSignatureAutomatically = () => {
    const canvas = canvasRef.current;

    if (!canvas || !hasCanvasSignature(canvas)) {
      setSignature(null);
      setHasSignature(false);
      return;
    }

    // Converter canvas para blob (PNG)
    canvas.toBlob((blob) => {
      if (blob) {
        // Criar arquivo de imagem PNG
        const signatureFile = new File([blob], 'signature.png', { type: 'image/png' });
        setSignature(signatureFile);
        setHasSignature(true);
      }
    }, 'image/png');
  };

  // Função para limpar a assinatura
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setSignature(null);
      setHasSignature(false);
    }
  };

  // NOVA FUNÇÃO: Upload da assinatura usando o novo endpoint
  const handleSignatureUpload = async () => {
    if (!signature) return;

    setIsUploadingSignature(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append('signature_picture', signature);

      console.log('Fazendo upload da assinatura para o novo endpoint...');

      const response = await axios.post(
        `https://new.cajuu.app/api/driver-signature-picture`,
        formData,
        {
          headers: {
            "Screen-Name": "/Profile",
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log('Resposta do upload da assinatura:', response.data);

      if (response.data) {
        if (response.data.message && response.data.message.includes('successfully')) {
          toast.success("Signature uploaded successfully");
        }
        else if (response.data.success) {
          toast.success("Signature uploaded successfully");
        }
        else if (response.data.message) {
          toast.success(response.data.message);
        }
        else {
          toast.success("Signature uploaded successfully");
        }

        const signaturePath =
          response.data.data?.signature_path ||
          response.data.signature_path ||
          response.data.data?.signature ||
          response.data.signature;

        if (signaturePath) {
          let fullSignatureUrl;
          if (signaturePath.startsWith('http')) {
            fullSignatureUrl = signaturePath;
          } else if (signaturePath.startsWith('/')) {
            fullSignatureUrl = `https://new.cajuu.app/storage${signaturePath}`;
          } else {
            fullSignatureUrl = `https://new.cajuu.app/storage/${signaturePath}`;
          }
          setExistingSignatureUrl(fullSignatureUrl);
          setExistingSignature(signaturePath);
          console.log('URL da assinatura atualizada:', fullSignatureUrl);
        }
      } else {
        toast.success("Signature uploaded successfully");
      }

    } catch (error) {
      console.error("Erro ao fazer upload da assinatura", error);

      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (errorData.message && errorData.message.includes('successfully')) {
          toast.success("Signature uploaded successfully");
          console.log('Upload considerado bem-sucedido apesar do status de erro');

          const signaturePath =
            errorData.data?.signature_path ||
            errorData.signature_path ||
            errorData.data?.signature ||
            errorData.signature;

          if (signaturePath) {
            let fullSignatureUrl;
            if (signaturePath.startsWith('http')) {
              fullSignatureUrl = signaturePath;
            } else if (signaturePath.startsWith('/')) {
              fullSignatureUrl = `https://new.cajuu.app/storage${signaturePath}`;
            } else {
              fullSignatureUrl = `https://new.cajuu.app/storage/${signaturePath}`;
            }
            setExistingSignatureUrl(fullSignatureUrl);
            setExistingSignature(signaturePath);
          }
        } else {
          toast.error(`Error: ${errorData.message || "Upload failed"}`);
        }
      } else if (error.request) {
        toast.error("Network error - please check your connection");
      } else {
        toast.error("Error uploading signature");
      }
    } finally {
      setIsUploadingSignature(false);
    }
  };

  // Efeito para inicializar o canvas quando abrir o drawer em modo de edição
  useEffect(() => {
    if (isOpen && isEditing && canvasRef.current && showSignatureCanvas) {
      initializeCanvas();
    }
  }, [isOpen, isEditing, showSignatureCanvas]);

  // Efeito para adicionar e remover event listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isOpen || !isEditing || !showSignatureCanvas) return;

    // Adicionar event listeners
    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseout', stopDrawing);

    // Event listeners para touch
    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    // Cleanup function
    return () => {
      if (canvas) {
        canvas.removeEventListener('mousedown', startDrawing);
        canvas.removeEventListener('mousemove', draw);
        canvas.removeEventListener('mouseup', stopDrawing);
        canvas.removeEventListener('mouseout', stopDrawing);

        canvas.removeEventListener('touchstart', handleTouchStart);
        canvas.removeEventListener('touchmove', handleTouchMove);
        canvas.removeEventListener('touchend', handleTouchEnd);
      }

      if (signatureTimeoutRef.current) {
        clearTimeout(signatureTimeoutRef.current);
      }
    };
  }, [isOpen, isEditing, isDrawing, showSignatureCanvas]);

  // Buscar assinatura quando entrar no modo de edição
  useEffect(() => {
    if (isOpen && isEditing) {
      // Pequeno delay para garantir que o canvas foi inicializado
      setTimeout(() => {
        fetchDriverSignature();
      }, 100);
    }
  }, [isOpen, isEditing]);

  // Função para buscar dados da empresa
  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("token");
    } catch (error) {
      console.error("Erro ao buscar dados da empresa", error);
      toast.error("Error fetching company data");
    }
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeDrawer();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      fetchCompanyData();
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const openDrawer = () => setIsOpen(true);

  const closeDrawer = () => {
    setIsOpen(false);
    setIsEditing(false);
    setImageError(false);
    setIsCanvasInitialized(false);
    setIsDrawing(false);
    setShowSignatureCanvas(false);
    setDrawerImageLoading(false);
    setDrawerImageError(false);
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      closeDrawer();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "phone") {
      // Aplicar máscara
      const formatted = formatPhoneNumber(value);
      setFormattedPhone(formatted);
      
      // Salvar valor sem formatação
      const unformatted = removePhoneFormatting(value);
      setProfile(prev => ({ 
        ...prev, 
        [name]: unformatted || "Not Provided"
      }));
    } else {
      setProfile(prev => ({ ...prev, [name]: value }));
    }
  };

  // MODIFICADO: Função para salvar alterações com diferentes endpoints baseados no role
  const handleSaveChanges = async () => {
    try {
      const token = localStorage.getItem("token");
      const userId = user.user_id || user.id;
      const userRoleId = window.userRoleId;

      if (!userId) {
        toast.error("User ID not found");
        return;
      }

      // Obter telefone sem formatação
      const phoneToSend = removePhoneFormatting(profile.phone);

      // Se o userRoleId for 2 ou 3, usar o endpoint específico
      if (userRoleId === 2 || userRoleId === 3) {
        // Endpoint para roles 2 e 3
        const response = await axios.post(
          `https://new.cajuu.app/api/users/profile`,
          {
            first_name: profile.firstName,
            last_name: profile.lastName,
            phone: phoneToSend === "" ? "" : phoneToSend,
          },
          {
            headers: {
              "Screen-Name": "/Profile",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          toast.success("Profile updated successfully");
          setIsEditing(false);
          setShowSignatureCanvas(false);
          fetchUserData(); // Recarregar dados do usuário
        }
      } else {
        // Para outros roles, usar o endpoint original
        const response = await axios.post(
          `https://new.cajuu.app/api/users/${userId}`,
          {
            first_name: profile.firstName,
            last_name: profile.lastName,
            email: profile.email,
            username: profile.username,
            phone: phoneToSend === "" ? "" : phoneToSend,
            profile_picture_path: profile.profile_image,
          },
          {
            headers: {
              "Screen-Name": "/User",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          // Se há uma nova assinatura e o role for 1, 4 ou 7, fazer upload
          if (signature && [1, 4, 7].includes(userRoleId)) {
            await handleSignatureUpload();
          }

          toast.success("Profile updated successfully");
          setIsEditing(false);
          setShowSignatureCanvas(false);
          fetchUserData();
        }
      }
    } catch (error) {
      console.error("Erro ao salvar alterações", error);
      if (error.response) {
        toast.error(`Error: ${error.response.data.message || "Failed to save changes"}`);
      } else {
        toast.error("Error saving changes");
      }
    }
  };

  const handleFileChange = async (event) => {
  const selectedFile = event.target.files[0];
  if (selectedFile) {
    setIsImageLoading(true);
    setImageError(false);
    setDrawerImageLoading(true);
    setDrawerImageError(false);

    const formData = new FormData();
    formData.append("profile_picture", selectedFile);

    try {
      const token = localStorage.getItem("token");
      console.log("Iniciando upload da imagem...");

      const uploadResponse = await axios.post(
        `https://new.cajuu.app/api/user-profile-picture`,
        formData,
        {
          headers: {
            "Screen-Name": "/Profile",
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (uploadResponse.data && uploadResponse.data.data) {
        const profilePicturePath = uploadResponse.data.data.profile_picture_path;

        const userId = user.user_id || user.id;
        const userRoleId = window.userRoleId;

        if (!userId) {
          throw new Error("User ID não encontrado");
        }

        const userToken = localStorage.getItem("token");
        
        // Obter telefone sem formatação
        const phoneToSend = removePhoneFormatting(profile.phone);
        
        // Se o userRoleId for 2 ou 3, usar o endpoint específico
        if (userRoleId === 2 || userRoleId === 3) {
          // Endpoint para roles 2 e 3
          await axios.post(
            `https://new.cajuu.app/api/users/profile`,
            {
              profile_picture_path: profilePicturePath,
              first_name: profile.firstName,
              last_name: profile.lastName,
              phone: phoneToSend === "" ? "" : phoneToSend,
              // Note: email e username NÃO são enviados para roles 2 e 3
            },
            {
              headers: {
                "Screen-Name": "/Profile",
                Authorization: `Bearer ${userToken}`,
              },
            }
          );
        } else {
          // Para outros roles, usar o endpoint original
          await axios.post(
            `https://new.cajuu.app/api/users/${userId}`,
            {
              profile_picture_path: profilePicturePath,
              first_name: profile.firstName,
              last_name: profile.lastName,
              email: profile.email,
              username: profile.username,
              phone: phoneToSend === "" ? "" : phoneToSend,
            },
            {
              headers: {
                "Screen-Name": "/User",
                Authorization: `Bearer ${userToken}`,
              },
            }
          );
        }

        setProfile(prev => ({
          ...prev,
          profile_image: profilePicturePath
        }));

        toast.success("Image uploaded successfully");
      }
    } catch (error) {
      console.error("Erro ao fazer upload da imagem", error);
      setImageError(true);
      setDrawerImageError(true);

      if (error.response) {
        console.error("Detalhes do erro:", error.response.data);
        toast.error(`Error: ${error.response.data.message || "Upload failed"}`);
      } else if (error.request) {
        toast.error("Network error - please check your connection");
      } else {
        toast.error("Error uploading image");
      }
    } finally {
      setIsImageLoading(false);
      setDrawerImageLoading(false);
    }
  }
};

  const drawerContent = (
    <div
      className={`fixed inset-0 z-50 overflow-hidden ${isOpen ? "opacity-100" : "opacity-0"
        }`}
      style={{
        transition: "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        pointerEvents: isOpen ? "auto" : "none",
      }}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-40"
        onClick={handleOverlayClick}
      >
        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
          <div
            ref={drawerRef}
            className="pointer-events-auto w-screen max-w-[90%] sm:max-w-md bg-[#1c1b19] m-3 rounded-md shadow-xl transition-transform duration-300 ease-in-out"
            style={{
              transform: isOpen ? "translateX(0)" : "translateX(100%)",
              transition: "transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          >
            <div className="flex h-full flex-col">
              <div className="p-4 sm:p-6 overflow-auto flex flex-col justify-between h-full">
                <div>
                  <header className="flex justify-between items-center">
                    <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-white">
                      Profile
                    </h1>
                    <button
                      onClick={closeDrawer}
                      className="p-1 bg-zinc-100 rounded-full transition-transform duration-200 ease-in-out hover:scale-110 active:scale-95"
                    >
                      <TbX className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                  </header>

                  {/* Seção do Perfil Pessoal */}
                  <div className="py-6 sm:py-6 flex flex-col w-full border border-zinc-500 rounded-xl mt-4 sm:mt-6 justify-center items-center fade-in relative">
                    <div className="relative group">
                      {/* Loading Spinner para drawer */}
                      {drawerImageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}

                      {/* Imagem do perfil ou placeholder */}
                      <div className={`w-20 sm:w-20 aspect-square rounded-full ${drawerImageLoading ? 'opacity-30' : 'opacity-100'} transition-opacity duration-300`}>
                        {!drawerImageError && profile.profile_image ? (
                          <img
                            src={`https://new.cajuu.app/storage/${profile.profile_image}?t=${Date.now()}`}
                            alt="Avatar"
                            className="w-full h-full rounded-full object-cover"
                            onLoad={() => setDrawerImageLoading(false)}
                            onError={() => {
                              setDrawerImageLoading(false);
                              setDrawerImageError(true);
                            }}
                          />
                        ) : (
                          <div className="w-full h-full rounded-full bg-gray-600 flex items-center justify-center">
                            <TbCamera className="w-8 h-8 text-white" />
                          </div>
                        )}
                      </div>

                      {/* Overlay da câmera - só mostra quando não está carregando */}
                      {!drawerImageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                          <label
                            htmlFor="profile-image-upload"
                            className="cursor-pointer"
                          >
                            <TbCamera className="w-6 h-6 text-white" />
                          </label>
                          <input
                            id="profile-image-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileChange}
                            disabled={drawerImageLoading}
                          />
                        </div>
                      )}
                    </div>
                    <div className="mt-4 text-center w-full">
                      {isEditing ? (
                        <div className="space-y-3 sm:space-y-3 px-2 sm:px-4 w-full">
                          {/* Campo Username - Apenas para roles 1, 4, 7 */}
                          {![2, 3].includes(window.userRoleId) && (
                            <div className="relative w-full">
                              <Label
                                htmlFor="username"
                                className="absolute -top-2 text-[0.7rem] bg-[#1c1b19] px-1 left-1 text-white"
                              >
                                Username <span className="text-rose-500">*</span>
                              </Label>
                              <Input
                                id="username"
                                name="username"
                                className="w-full text-white bg-transparent border-zinc-500"
                                placeholder="Username"
                                value={profile.username}
                                onChange={handleInputChange}
                              />
                            </div>
                          )}

                          {/* Campo Email - Apenas para roles 1, 4, 7 */}
                          {![2, 3].includes(window.userRoleId) && (
                            <div className="relative w-full">
                              <Label
                                htmlFor="email"
                                className="absolute -top-2 text-[0.7rem] bg-[#1c1b19] px-1 left-1 text-white"
                              >
                                Email <span className="text-rose-500">*</span>
                              </Label>
                              <Input
                                id="email"
                                name="email"
                                className="w-full text-white bg-transparent border-zinc-500"
                                placeholder="Email"
                                value={profile.email}
                                onChange={handleInputChange}
                              />
                            </div>
                          )}

                          {/* Exibir email e username como texto para roles 2 e 3 no modo de edição */}
                          {[2, 3].includes(window.userRoleId) && (
                            <>
                              <div className="relative w-full text-left">
                                <Label
                                  className="absolute -top-2 text-[0.7rem] bg-[#1c1b19] px-1 left-1 text-white"
                                >
                                  Username
                                </Label>
                                <div className="w-full text-white bg-transparent border-zinc-500 border rounded-md p-2 mt-1 text-sm">
                                  {profile.username}
                                </div>
                              </div>

                              <div className="relative w-full text-left">
                                <Label
                                  className="absolute -top-2 text-[0.7rem] bg-[#1c1b19] px-1 left-1 text-white"
                                >
                                  Email
                                </Label>
                                <div className="w-full text-white bg-transparent border-zinc-500 border rounded-md p-2 mt-1 text-sm">
                                  {profile.email}
                                </div>
                              </div>
                            </>
                          )}

                          <div className="relative w-full">
                            <Label
                              htmlFor="firstName"
                              className="absolute -top-2 text-[0.7rem] bg-[#1c1b19] px-1 left-1 text-white"
                            >
                              First Name <span className="text-rose-500">*</span>
                            </Label>
                            <Input
                              id="firstName"
                              name="firstName"
                              className="w-full text-white bg-transparent border-zinc-500"
                              placeholder="First Name"
                              value={profile.firstName}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="relative w-full">
                            <Label
                              htmlFor="lastName"
                              className="absolute -top-2 text-[0.7rem] bg-[#1c1b19] px-1 left-1 text-white"
                            >
                              Last Name <span className="text-rose-500">*</span>
                            </Label>
                            <Input
                              id="lastName"
                              name="lastName"
                              className="w-full text-white bg-transparent border-zinc-500"
                              placeholder="Last Name"
                              value={profile.lastName}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="relative w-full">
                            <Label
                              htmlFor="phone"
                              className="absolute -top-2 text-[0.7rem] bg-[#1c1b19] px-1 left-1 text-white"
                            >
                              Phone
                            </Label>
                            <Input
                              id="phone"
                              name="phone"
                              className="w-full text-white bg-transparent border-zinc-500"
                              placeholder="xxx-xxx-xxxx"
                              value={formattedPhone}
                              onChange={handleInputChange}
                              maxLength={14}
                              onKeyDown={(e) => {
                                // Permitir apenas números, backspace, delete, tab e setas
                                if (!/[0-9\b\t-]/.test(e.key) && 
                                    e.key !== 'Backspace' && 
                                    e.key !== 'Delete' && 
                                    e.key !== 'Tab' &&
                                    e.key !== 'ArrowLeft' &&
                                    e.key !== 'ArrowRight' &&
                                    e.key !== 'ArrowUp' &&
                                    e.key !== 'ArrowDown' &&
                                    e.key !== 'Home' &&
                                    e.key !== 'End') {
                                  e.preventDefault();
                                }
                              }}
                              onPaste={(e) => {
                                e.preventDefault();
                                const pastedData = e.clipboardData.getData('text');
                                const numbersOnly = pastedData.replace(/\D/g, '');
                                const formatted = formatPhoneNumber(numbersOnly);
                                
                                // Atualizar estados
                                setFormattedPhone(formatted);
                                setProfile(prev => ({ 
                                  ...prev, 
                                  phone: numbersOnly.slice(0, 10) || "Not Provided"
                                }));
                              }}
                            />
                            
                          </div>

                          {/* Seção de Assinatura Digital - Apenas para roles 1, 4, 7 */}
                         {[1, 2, 7].includes(user.user_role_id) && isEditing && (
                            <div className="mt-4 pt-4 border-t border-zinc-500">
                              <label className="block text-sm font-medium text-white mb-2 text-center">
                                Digital Signature
                              </label>

                              {isLoadingSignature ? (
                                <div className="border-2 border-gray-600 rounded-lg bg-white mb-2 w-full h-32 flex items-center justify-center">
                                  <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                                  <span className="text-gray-500 text-xs ml-2">Loading signature...</span>
                                </div>
                              ) : (
                                <>
                                  {/* Mostrar imagem da assinatura se existir e não estiver mostrando o canvas */}
                                  {existingSignatureUrl && !showSignatureCanvas && (
                                    <div
                                      className="cursor-pointer"
                                      onClick={showCanvasAndLoadSignature}
                                    >
                                      <label className="block text-sm font-medium text-white mb-2 text-center">
                                        Your Current Signature (Click to edit)
                                      </label>
                                      <div className="border-2 border-gray-600 rounded-lg bg-white p-2">
                                        <img
                                          src={existingSignatureUrl}
                                          alt="Current Signature"
                                          className="w-full h-20 object-contain mx-auto"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                      <p className="text-white text-xs mt-1 text-center">
                                        Click on the signature to edit it
                                      </p>
                                    </div>
                                  )}

                                  {/* Mostrar canvas para assinar quando não há assinatura ou quando o usuário clicou para editar */}
                                  {(!existingSignatureUrl || showSignatureCanvas) && (
                                    <>
                                      <div className="border-2 border-gray-600 rounded-lg bg-white mb-2 w-full">
                                        <canvas
                                          ref={canvasRef}
                                          className="w-full h-32 cursor-crosshair"
                                          style={{
                                            touchAction: 'none',
                                            background: '#ffffff'
                                          }}
                                        />
                                      </div>

                                      <p className="text-white text-xs mb-3 text-center">
                                        {existingSignature
                                          ? "Edit your signature in the box above"
                                          : "Draw your signature in the box above - It will be saved automatically"
                                        }
                                      </p>

                                      <div className="flex justify-center">
                                        <SecondaryButton
                                          onClick={clearSignature}
                                          className="text-gray-400 hover:text-white text-xs"
                                        >
                                          Clear Signature
                                        </SecondaryButton>
                                      </div>

                                      {hasSignature && (
                                        <div className="text-green-500 text-xs mt-2 text-center">
                                          ✓ Signature {existingSignature ? 'updated' : 'saved'} automatically
                                        </div>
                                      )}

                                      {/* Loading indicator durante o upload */}
                                      {isUploadingSignature && (
                                        <div className="text-amber-500 text-xs mt-2 text-center">
                                          ⏳ Uploading signature...
                                        </div>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                           )}  
                        </div>
                      ) : (
                        <>
                          <h2 className="text-base sm:text-lg font-semibold tracking-tight text-white">
                            {profile.firstName} {profile.lastName}
                          </h2>
                          <p className="text-xs sm:text-sm text-white mt-1">
                            {profile.email}
                          </p>
                          <p className="text-xs sm:text-sm text-white mt-1">
                            {profile.username}
                          </p>
                          <p className="text-xs sm:text-sm text-white mt-1">
                            {profile.phone === "Not Provided" ? "Not Provided" : formatPhoneNumber(profile.phone)}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Seção da Empresa */}
                  <div className="py-6 sm:py-6 flex flex-col w-full border border-zinc-500 rounded-xl mt-4 sm:mt-4 justify-center items-center fade-in relative">
                    <div className="flex items-center justify-center mb-3">
                      <TbBuilding className="w-8 h-8 text-white mr-2" />
                      <h3 className="text-base sm:text-lg font-semibold tracking-tight text-white">
                        Company Information
                      </h3>
                    </div>
                    <div className="mt-2 text-center w-full">
                      <h2 className="text-sm sm:text-base font-semibold tracking-tight text-white">
                        {user.partner_company_name}
                      </h2>
                      <p className="text-xs sm:text-sm text-white mt-1">
                        {user.user_role_name}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="fade-in space-y-3 sm:space-y-3 mt-4 sm:mt-4">
                  {isEditing ? (
                    <>
                      <SecondaryButton
                        onClick={handleSaveChanges}
                        disabled={isUploadingSignature}
                        className="bg-green-500 border-none text-slate-900 w-full justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 text-sm sm:text-base py-2 sm:py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUploadingSignature ? "Uploading Signature..." : "Save Changes"}
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => {
                          setIsEditing(false);
                          setShowSignatureCanvas(false);
                          // Restaurar formatação do telefone
                          setFormattedPhone(
                            profile.phone === "Not Provided" 
                              ? "Not Provided" 
                              : formatPhoneNumber(profile.phone)
                          );
                        }}
                        className="bg-gray-500 border-none text-slate-900 w-full justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 text-sm sm:text-base py-2 sm:py-2"
                      >
                        Cancel
                      </SecondaryButton>
                    </>
                  ) : (
                    <SecondaryButton
                      onClick={() => setIsEditing(true)}
                      className="bg-blue-500 border-none text-slate-900 w-full justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 text-sm sm:text-base py-2 sm:py-2"
                    >
                      Edit Profile
                    </SecondaryButton>
                  )}
                  <SecondaryButton
                    onClick={() => {
                      logout();
                      closeDrawer();
                      navigate("/login");
                    }}
                    className="bg-amber-500 border-none text-slate-900 w-full justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 text-sm sm:text-base py-2 sm:py-2"
                  >
                    Log out
                  </SecondaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={openDrawer}
        className="transition-transform duration-200 ease-in-out hover:scale-110 active:scale-95"
      >
        {headerImageLoading && (
          <div className="aspect-square w-6 sm:w-8 ml-2 sm:ml-4 mr-2 rounded-full sm:mr-4 bg-gray-600 flex items-center justify-center">
            <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!headerImageLoading && !headerImageError && profile.profile_image ? (
          <img
            src={`https://new.cajuu.app/storage/${profile.profile_image}?t=${Date.now()}`}
            alt="Avatar"
            className="aspect-square w-6 sm:w-8 ml-2 sm:ml-4 mr-2 rounded-full sm:mr-4"
          />
        ) : (
          !headerImageLoading && (
            <div className="aspect-square w-6 sm:w-8 ml-2 sm:ml-4 mr-2 rounded-full sm:mr-4 bg-gray-600 flex items-center justify-center">
              <TbCamera className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
            </div>
          )
        )}
      </button>
      {createPortal(drawerContent, document.body)}
    </>
  );
};

export default ProfileDrawer;